import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import { styledReset, styledTypography } from '@/primitives';
import { type GameStates } from '@/hooks/fantasyWishlists';

interface Props {
    $isLoading?: boolean;
    $status?: GameStates;
    $disabled?: boolean;
}

export const InnerCardContent = styled(motion.div)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    padding: 20px;
    gap: 6px;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 30px;
        gap: 10px;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletSmall}) {
        padding: 30px;
    }
`;

export const HeadingWrapper = styled.div`
    gap: 10px;
    width: 100%;
    max-width: 100%;
    padding-bottom: 5px;
    margin-top: 5px;
    justify-content: space-between;
    flex-wrap: nowrap;
    display: flex;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    flex-grow: 1;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletSmall}) {
        padding-bottom: 15px;
    }
`;

export const HeadingTextWrapper = styled.div`
    display: inline-flex;
    flex-shrink: 2;
    flex-direction: column;
    gap: 0;
    width: auto;
    overflow: hidden;
`;

export const Heading = styled.div<Props>`
    font-weight: 700;
    font-size: 18px;
    line-height: 120%;

    overflow: hidden;
    padding-bottom: 5px;
    padding-top: 1px;

    ${({ $isLoading }) => {
        if ($isLoading)
            return css`
                width: 220px;
                margin-top: 5px;

                border-radius: 3px;
                background: rgba(255, 255, 255, 0.05);
            `;
    }};

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletSmall}) {
        font-size: 24px;
    }
`;

export const GameNumber = styled.div<Props>`
    font-weight: 400;
    font-size: 11px;
    line-height: 17px;
    letter-spacing: -0.02em;
    color: rgba(255, 255, 255, 0.5);
    height: 15px;
    ${({ $isLoading }) => {
        if ($isLoading)
            return css`
                width: 110px;
                border-radius: 3px;
                background: rgba(255, 255, 255, 0.05);
            `;
    }};
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletSmall}) {
        font-size: 15px;
        line-height: 100%;
    }
`;

export const Image = styled.img`
    width: 100%;
    height: 160px;
    overflow: hidden;
    border-radius: 10px;
    margin-top: 5px;
    background: rgba(255, 255, 255, 0.05);
    object-fit: cover;
    object-position: center;
    img {
        width: 100%;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        height: 213px;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.mobileSmall}) {
        height: 180px;
    }
`;

export const PrizeRowWrapper = styled.div<Props>`
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    white-space: nowrap;
    overflow: hidden;

    flex-wrap: wrap;

    width: 100%;
    gap: 8px;
    margin-top: 5px;

    ${({ $isLoading }) => {
        if ($isLoading)
            return css`
                width: 180px;
                border-radius: 5px;
                background: rgba(255, 255, 255, 0.05);
            `;
    }};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        height: 24px;
    }
`;

export const PrizesValueLabel = styled.div`
    font-weight: 400;
    font-size: 11px;
    line-height: 17px;
    letter-spacing: -0.02em;
    color: rgba(255, 255, 255, 0.5);
    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 13px;
        line-height: 149%;
    }
`;

export const PrizeValuePill = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    background: #ffffff;
    border-radius: 100px;
    font-weight: 700;
    font-size: 9px;
    line-height: 11px;
    letter-spacing: -0.02em;
    color: #131313;
    padding: 3px 6px 4px 6px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 12px;
        line-height: 130%;
        padding: 4px 8px;
    }
`;

export const GameCardCTA = styled.div<Props>`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 30px 0 24px;
    gap: 8px;
    margin-top: 5px;
    width: 100%;
    border-radius: 40px;
    cursor: inherit;
    opacity: 0.95;
    transition:
        box-shadow 0.2s ease,
        opacity 0.2s ease;
    box-shadow:
        0 0 68px 0 rgba(255, 255, 255, 0) inset,
        0 0 0 1px rgba(255, 255, 255, 0) inset;

    &:hover {
        box-shadow:
            0 0 68px 0 rgba(255, 255, 255, 0.05) inset,
            0 0 0 1px rgba(255, 255, 255, 0.05) inset;

        opacity: 1;
    }
    line-height: 120%;
    text-align: center;
    letter-spacing: -0.03em;
    color: #ffffff;
    height: 41px;

    ${({ $disabled }) =>
        $disabled &&
        css`
            cursor: not-allowed;
        `}

    ${({ $status }) => {
        switch ($status) {
            case 'closed': {
                return css`
                    font-weight: 700;
                    background: radial-gradient(
                            67.31% 175% at 50% 148.75%,
                            rgba(255, 255, 255, 0.001) 0%,
                            rgba(0, 0, 0, 0) 100%
                        ),
                        rgba(255, 255, 255, 0.1);
                    font-size: 13px;
                    @media (min-width: ${({ theme }) => theme.breakpoints.mobileSmall}) {
                        font-size: 18px;
                    }
                `;
            }

            case 'open':
            case 'playing': {
                return css`
                    font-weight: 600;
                    background: linear-gradient(300.86deg, #523df1 39.32%, #976af6 92.76%);
                    font-size: 13px;
                    @media (min-width: ${({ theme }) => theme.breakpoints.mobileSmall}) {
                        font-size: 16px;
                    }
                `;
            }

            case 'ended':
            default: {
                return css`
                    font-weight: 700;
                    background: radial-gradient(
                            67.31% 175% at 50% 148.75%,
                            rgba(255, 255, 255, 0.001) 0%,
                            rgba(0, 0, 0, 0) 100%
                        ),
                        rgba(255, 255, 255, 0.1);
                    font-size: 13px;
                    @media (min-width: ${({ theme }) => theme.breakpoints.mobileSmall}) {
                        font-size: 18px;
                    }
                `;
            }
        }
    }}

    ${({ $isLoading }) => {
        if ($isLoading)
            return css`
                background: rgba(255, 255, 255, 0.05);
            `;
    }};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileSmall}) {
        height: 56px;
    }
`;

export const EmptyStateWrapper = styled.div`
    display: flex;
    padding: 0 0 20px;
`;

export const EmptyText = styled.div`
    ${styledTypography.allianceBase};
    font-weight: 600;
    font-size: 18px;
    opacity: 0.5;
`;

const WRAPPER_STYLES = css<Props>`
    ${styledReset.link};
    width: 100%;
    transition: all 0.3s ease-in-out;
    &:hover {
        box-shadow: 8px 10px 14px black;
        ${GameCardCTA} {
            box-shadow:
                0 0 68px 0 rgba(255, 255, 255, 0.05) inset,
                0 0 0 1px rgba(255, 255, 255, 0.05) inset;
            opacity: 1;
        }
    }

    ${({ $disabled }) =>
        $disabled &&
        css`
            pointer-events: none;
        `}
`;

export const WrapperLink = styled(Link)<Props>`
    ${WRAPPER_STYLES}
`;

export const WrapperExternalLink = styled.a.attrs<Props>({
    target: '_blank',
    rel: 'noopener noreferrer',
})`
    ${WRAPPER_STYLES}
`;

export const Wrapper = styled.div<Props>`
    // leaving this in for the Skeleton Cards
    ${WRAPPER_STYLES}
`;
